<template>
  <v-container fluid class="bonos-root">
    <!--  Cartas superiores -->
    <v-row dense class="mb-4">
      <!-- Personas con un bono o más -->
      <v-col cols="12" sm="6" md="4" lg="4">
        <v-card class="pa-4 summary-card" elevation="2">
          <div class="d-flex align-center">
            <v-avatar size="36" class="mr-3" color="orange lighten-5">
              <v-icon color="orange darken-2">mdi-account-group</v-icon>
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-bold">{{ personasConBonoCount }}</div>
              <div class="text-caption grey--text">Personas con bonos</div>
            </div>
          </div>
        </v-card>
      </v-col>
      <!-- Costo del bono más caro -->
      <v-col cols="12" sm="6" md="4" lg="4">
        <v-card class="pa-4 summary-card" elevation="2">
          <div class="d-flex align-center">
            <v-avatar size="36" class="mr-3" color="red lighten-5">
              <v-icon color="red darken-2">mdi-cash</v-icon>
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-bold">{{ fmtCurrency(bonoMasCaroMonto) }}</div>
              <div class="text-caption grey--text">Bono más caro</div>
            </div>
          </div>
        </v-card>
      </v-col>
      <!-- Costo del bono más barato -->
      <v-col cols="12" sm="6" md="4" lg="4">
        <v-card class="pa-4 summary-card" elevation="2">
          <div class="d-flex align-center">
            <v-avatar size="36" class="mr-3" color="deep-orange lighten-5">
              <v-icon color="deep-orange darken-2">mdi-cash-minus</v-icon>
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-bold">{{ fmtCurrency(bonoMasBaratoMonto) }}</div>
              <div class="text-caption grey--text">bono más barato</div>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12" md="7" lg="8">
        <v-card elevation="2" class="bonos-card">
          <!-- Cantidad de bonos activos y boton para crear bono -->
          <v-card-text>
            <div class="d-flex align-center justify-space-between">
              <div class="text-h6 font-weight-bold">
                {{ filteredBonos.length }} Bono(s) registrado(s)
              </div>

              <div class="d-flex align-center tools-row">
                <v-btn color="primary" @click="abrirCrear()">Agregar bono</v-btn>
              </div>
            </div>
          </v-card-text>
          <v-divider />

          <!-- Tabla de bonos filtros -->
          <v-card-text class="pt-4">
            <v-data-table :headers="bonosHeaders" :items="bonosTableItems" :items-per-page="6" class="elevation-0"
              hide-default-footer :loading="loadingBonos">
              <template v-slot:top>
                <div class="d-flex align-center mb-3 filters-row">
                  <v-text-field v-model="bonosSearch" dense outlined hide-details prepend-inner-icon="mdi-magnify"
                    label="Buscar bono" class="filter-input" />
                  <v-autocomplete v-model="bonoConceptoSearch" :items="conceptosBonos" item-text="nombre"
                    item-value="idConceptoBono" label="Concepto" outlined dense multiple small-chips clearable hide-details
                    prepend-inner-icon="mdi-shape-outline" class="filter-select">
                    <template v-slot:selection="{ item }">
                      <div class="d-flex align-center">
                        <v-avatar size="22" class="mr-2" :color="item.color + ' lighten-5'">
                          <v-icon small :color="item.color + ' darken-2'">{{ item.icon }}</v-icon>
                        </v-avatar>
                        <span class="font-weight-medium">{{ item.descripcion }}</span>
                      </div>
                    </template>

                    <template v-slot:item="{ item }">
                      <div class="d-flex align-center py-2">
                        <v-avatar size="22" class="mr-2" :color="item.color + ' lighten-5'">
                          <v-icon small :color="item.color + ' darken-2'">{{ item.icon }}</v-icon>
                        </v-avatar>
                        <div>
                          <div class="font-weight-medium">{{ item.descripcion }}</div>
                          <div class="text-caption grey--text">{{ item.detalles }}</div>
                        </div>
                      </div>
                    </template>
                  </v-autocomplete>
                  <v-spacer />
                </div>
              </template>
              <!-- Código y nombre del bono -->
              <template v-slot:item.bono="{ item }">
                <div class="d-flex align-center">
                  <div>
                    <div class="text-caption blue--text text--darken-2">{{ item.codigo }}</div>
                    <div class="font-weight-bold">{{ item.nombre }}</div>
                  </div>
                </div>
              </template>

              <template v-slot:item.periodicidad="{ item }">
                <div class="d-flex align-center justify-center">
                  <v-chip x-small outlined color="indigo">
                    {{ fmtPeriodicidad(item.periodicidadTipo, item.periodicidadConfig) }}
                  </v-chip>
                </div>
              </template>

              <template v-slot:item.tipoCalculo="{ item }">
                <div class="d-flex align-center justify-center">
                  <v-chip x-small outlined color="teal">
                    {{ fmtTipoCalculo(item.tipoCalculo) }}
                  </v-chip>
                </div>
              </template>

              <!-- Personas asignadas -->
              <template v-slot:item.personas="{ item }">
                <div class="d-flex align-center justify-center">
                  <v-tooltip bottom v-if="item.idsPersonas && item.idsPersonas.length" dark>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip v-bind="attrs" v-on="on" x-small outlined color="primary">
                        {{ item.idsPersonas.length }} persona{{ item.idsPersonas.length === 1 ? '' : 's' }}
                      </v-chip>
                    </template>

                    <div style="max-width: 320px;">
                      <div class="font-weight-bold mb-1 white--text">Personas asignadas</div>

                      <div v-if="item.nombresPersonas && item.nombresPersonas.length">
                        <div v-for="(n, i) in item.nombresPersonas" :key="i" class="white--text">
                          • {{ n }}
                        </div>
                      </div>

                      <div v-else class="white--text">
                        • Sin nombres disponibles
                      </div>
                    </div>
                  </v-tooltip>

                  <span v-else class="text-caption grey--text">—</span>
                </div>
              </template>

              <!-- Código y nombre del bono -->
              <!-- <template v-slot:item.fechaCreacion="{ item }">
                <div class="d-flex align-center justify-center">
                  <div class=" d-flex align-center justify-centertext-caption blue--text text--darken-2">{{
                    item.fechaCreacion }}</div>
                </div>
              </template> -->

              <!-- Listado de iconos por concepto de bonos -->
              <template v-slot:item.conceptos="{ item }">
                <div class="d-flex align-center justify-center">
                  <v-tooltip bottom v-if="item.idConceptoBono" dark>
                    <template v-slot:activator="{ on, attrs }">
                      <v-avatar size="38" class="mx-1" :color="getConceptColor(item.idConceptoBono, 'lighten-5')"
                        v-bind="attrs" v-on="on">
                        <v-icon :color="getConceptColor(item.idConceptoBono, 'darken-2')">
                          {{ getConceptIcon(item.idConceptoBono) }}
                        </v-icon>
                      </v-avatar>
                    </template>

                    <div style="max-width: 320px;">
                      <div class="font-weight-bold white--text">
                        {{ item.nombreConceptoBono || ("Concepto " + item.idConceptoBono) }}
                      </div>
                      <div class="text-caption white--text">
                        {{ getConceptDetalles(item.idConceptoBono) }}
                      </div>
                      <div class="text-caption mt-2 white--text" v-if="item.meta !== null && item.meta !== undefined">
                        Meta: <b>{{ item.meta }}</b>
                        <span v-if="item.tolerancia !== null && item.tolerancia !== undefined">
                          | Tolerancia: <b>{{ item.tolerancia }}</b>
                        </span>
                      </div>
                    </div>
                  </v-tooltip>

                  <v-tooltip bottom v-if="isKpiBono(item)" dark>
                    <template v-slot:activator="{ on, attrs }">
                      <v-avatar size="38" class="mx-1" color="indigo lighten-5" v-bind="attrs" v-on="on">
                        <v-icon color="indigo darken-2">
                          mdi-chart-line
                        </v-icon>
                      </v-avatar>
                    </template>
                    <div style="max-width: 320px;">
                      <div class="font-weight-bold white--text">
                        Bono KPI Operativo
                      </div>
                      <div class="text-caption white--text">
                        {{ getBonoConceptDetalles(item) }}
                      </div>
                      <div class="text-caption mt-2 white--text">
                        {{ getBonoKpiMetaLine(item) }}
                      </div>
                    </div>
                  </v-tooltip>

                  <span v-if="!item.idConceptoBono && !isKpiBono(item)" class="text-caption white--text">—</span>
                </div>
              </template>

              <!-- Personas con el bono y veces que no se han pagado -->
              <template v-slot:item.total="{ item }">
                <div class="d-flex align-center justify-center">
                  <v-chip class="mr-1" color="success">
                    $ {{ item.total }}
                  </v-chip>
                </div>
              </template>

              <!-- Acciones -->
              <template v-slot:item.acciones="{ item }">
                <div class="d-flex align-center justify-center">
                  <!-- Editar -->
                  <v-tooltip bottom v-if="false">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" class="mx-1" small @click.stop="editarBono(item)">
                        mdi-pencil
                      </v-icon>
                    </template>
                    <span class="white--text">Editar</span>
                  </v-tooltip>

                  <!-- Asignar -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" class="mx-1" small @click.stop="abrirAsignacionBono(item)">
                        mdi-account-plus
                      </v-icon>
                    </template>
                    <span class="white--text">Asignar</span>
                  </v-tooltip>

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" class="mx-1" small @click.stop="openDetails(item)">
                        mdi-eye
                      </v-icon>
                    </template>
                    <span class="white--text">Visualizar información</span>
                  </v-tooltip>

                  <!-- Eliminar -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" class="mx-1" small @click.stop="eliminarBono(item)">
                        mdi-delete
                      </v-icon>
                    </template>
                    <span class="white--text">Eliminar</span>
                  </v-tooltip>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Personas con bonos y sus cálculos -->
      <v-col cols="12" md="5" lg="4">
        <v-card elevation="2" class="bonos-card">
          <v-card-title class="py-3">
            <div class="d-flex align-center justify-space-between w-100">
              <div class="text-h6 font-weight-bold">Personas con Bono</div>
              <div class="text-subtitle-2 grey--text">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on" :loading="loadingPersonasBonos" @click="getBonosPersonas()">
                      <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                  </template>
                  <span class="white--text">Actualizar personas con bonos</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn dark color="primary" :loading="loadingExcel">
                      <v-icon v-bind="attrs" v-on="on" @click="getExcel()" >
                        mdi-download-circle-outline
                      </v-icon>
                    </v-btn>
                  </template>
                  <span class="white--text">Descargar Excel</span>
                </v-tooltip>
              </div>
              <!-- <div class="text-subtitle-2 grey--text">{{ filteredPeople.length }}</div> -->
            </div>
          </v-card-title>

          <v-divider />

          <v-card-text class="pt-4">
            <v-progress-linear v-if="loadingPersonasBonos" indeterminate class="mb-3" />
            <v-alert v-if="personasBonosError" dense outlined type="error" class="mb-3">
              {{ personasBonosError }}
            </v-alert>
            <v-text-field v-model="peopleSearch" dense outlined hide-details prepend-inner-icon="mdi-magnify"
              label="Buscar" class="mb-3" :disabled="loadingPersonasBonos"/>

            <v-list dense class="py-0" style="max-height:520px; overflow-y:auto;" :disabled="loadingPersonasBonos">
              <template v-for="(p, idx) in peopleListItems">
                <v-list-item class="px-2" @click="openPerson(p)">

                  <v-list-item-avatar>
                    <v-avatar size="36" color="grey lighten-4">
                      <v-icon color="grey darken-1">mdi-account</v-icon>
                    </v-avatar>
                  </v-list-item-avatar>

                  <v-list-item-content style="min-width:0;">
                    <v-list-item-title class="font-weight-medium">
                      {{ p.nombre }}
                      <v-icon v-if="p.tieneAlerta" color="amber darken-2" small class="ml-1"
                        title="No cumple requisitos, pero asignado">
                        mdi-alert
                      </v-icon>
                    </v-list-item-title>

                    <!-- <v-list-item-subtitle class="text-caption grey--text text-truncate">
                      {{ p.area }} · Bonos: {{ p.bonosCount }}
                    </v-list-item-subtitle> -->

                    <!-- chips en una sola línea con wrap controlado -->
                    <div class="mt-1" style="display:flex; gap:6px; flex-wrap:wrap; align-items:center;">
                      <v-chip x-small outlined color="green">
                        Pagado {{ fmtCurrency(Number(p.montoPagado || 0)) }}
                      </v-chip>

                      <v-chip x-small outlined color="amber">
                        Pendiente {{ fmtCurrency(Number(p.montoPendiente || 0)) }}
                      </v-chip>

                      <v-chip x-small outlined color="blue-grey">
                        Denegados {{ Number(p.noAplica || 0) }}
                      </v-chip>
                    </div>
                  </v-list-item-content>

                  <!-- Iconos a la derecha, compactos -->
                  <v-list-item-action class="ml-2" style="min-width:auto;">
                    <div style="display:flex; align-items:center; justify-content:flex-end; gap:6px;">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn x-small color="primary" outlined v-bind="attrs" v-on="on"
                            @click.stop="openPerson(p)">
                            Ver bonos
                          </v-btn>
                        </template>
                        <span class="white--text">Ver detalle de bonos y calendario</span>
                      </v-tooltip>

                      <v-tooltip v-for="b in (p.bonos || [])"
                        :key="b.idOperadorBono || (b.idBono + '_' + b.idConceptoBono)" bottom dark>
                        <template v-slot:activator="{ on, attrs }">
                          <v-avatar size="28" :color="getBonoConceptColor(b, 'lighten-5')" v-bind="attrs"
                            v-on="on">
                            <v-icon small :color="getBonoConceptColor(b, 'darken-2')">
                              {{ getBonoConceptIcon(b) }}
                            </v-icon>
                          </v-avatar>
                        </template>

                        <div style="max-width: 320px;">
                          <div class="font-weight-bold white--text">
                            {{ getBonoConceptTitle(b) }}
                          </div>
                          <div class="text-caption white--text">
                            {{ getBonoConceptDetalles(b) }}
                          </div>

                          <div class="text-caption white--text mt-2" v-if="isKpiBono(b)">
                            {{ getBonoKpiMetaLine(b) }}
                          </div>

                          <div class="text-caption white--text mt-2" v-else>
                            Meta: <b>{{ b.meta }}</b> | Tolerancia: <b>{{ b.tolerancia }}</b>
                          </div>
                          <div class="text-caption white--text" v-if="b.fechaAsignado">
                            Asignado: <b>{{ b.fechaAsignado }}</b>
                          </div>
                        </div>
                      </v-tooltip>

                      <span v-if="!(p.bonos && p.bonos.length)" class="text-caption grey--text">—</span>
                    </div>
                  </v-list-item-action>

                </v-list-item>

                <v-divider v-if="idx < peopleListItems.length - 1" :key="(p.idPersona || idx) + '-d'" />
              </template>
              <div v-if="!loadingPersonasBonos && (!peopleListItems || !peopleListItems.length)" class="text-caption grey--text pa-3">
                {{ personasConBonos.length ? "Sin resultados para el filtro actual" : "Aun no hay personas con bonos activos" }}
              </div>
            </v-list>

          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialogo para asignación de los bonos a las personas -->
    <v-dialog v-model="assignDialog">
      <v-card>
        <v-card-title class="py-3">
          Asignar bono
        </v-card-title>

        <v-card-text class="pt-4">
          <div class="d-flex align-center mb-3 assign-row">
            <div class="text-subtitle-2 grey--text">
              Bono seleccionado:
              <span class="font-weight-bold">{{ bonoSeleccionado.nombre }}</span>
            </div>
            <v-chip class="ml-3" outlined color="primary">
              Seleccionadas: <b class="ml-1">{{ (personasAAsignar || []).length }}</b>
            </v-chip>

            <v-spacer />

            <v-text-field v-model="assignSearch" dense outlined hide-details prepend-inner-icon="mdi-magnify"
              label="Buscar persona" class="assign-search" />
          </div>
          <v-data-table v-model="personasAAsignar" :headers="asignacionHeaders" :items="personasConRequisitos"
            item-key="idPersona" :search="assignSearch" show-select>
            <template v-slot:item.select="{ item }">
              <v-checkbox v-model="assignSelectedIds" :value="item.idPersona" hide-details dense />
            </template>

            <template v-slot:item.areas="{ item }">
              <div v-if="item.areas && item.areas.length">
                <v-chip v-for="id in item.areas" :key="id" class="ma-1" small label>
                  {{ areasById[id] || `Área ${id}` }}
                </v-chip>
              </div>
              <span v-else class="grey--text">—</span>
            </template>

            <template v-slot:item.requisitos="{ item }">
              <!-- NO CUMPLE: rojo + tooltip con motivos -->
              <v-tooltip bottom v-if="!item.cumple" style="background-color: black;">
                <template v-slot:activator="{ on, attrs }">
                  <v-chip x-small color="red" dark v-bind="attrs" v-on="on">
                    No cumple
                  </v-chip>
                </template>

                <div style="max-width: 380px;" class=white--text>
                  <div class="font-weight-bold mb-1">Motivos</div>
                  <div v-for="(r, i) in item.razonesNoCumple" :key="i">
                    • {{ r }}
                  </div>
                </div>
              </v-tooltip>

              <!-- CUMPLE: verde -->
              <v-chip v-else x-small color="green" dark>
                Cumple
              </v-chip>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions class="py-3 px-4">
          <v-spacer />
          <v-btn text color="success" @click="assignDialog = false" :loading="loadingAsignarBono">Cancelar</v-btn>
          <v-btn text color="error" dark depressed @click="asignarBono()" :loading="loadingAsignarBono">
            Asignar ({{ (personasAAsignar || []).length }})
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DIALOG: Confirmar eliminación -->
    <v-dialog v-model="dialogEliminarBono" persistent>
      <v-card>
        <v-card-title class="text-h6">
          Confirmar eliminación
        </v-card-title>

        <v-card-text>
          ¿Seguro que deseas eliminar este bono?
          <div class="mt-2" v-if="detailsBono">
            <div class="text-caption grey--text">Código</div>
            <div class="font-weight-bold">{{ detailsBono.codigo }}</div>

            <div class="text-caption grey--text mt-2">Nombre</div>
            <div class="font-weight-bold">{{ detailsBono.nombre }}</div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text color="success" @click="cerrarEliminarBono" :disabled="loadingEliminarBono">
            Cancelar
          </v-btn>
          <v-btn text color="red" dark @click="deleterBono" :loading="loadingEliminarBono">
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="detailsDialog" max-width="760">
      <v-card>
        <v-card-title class="text-h6">Información del bono</v-card-title>
        <v-card-text v-if="detailsBono">
          <v-row dense>
            <v-col cols="12" md="6">
              <div class="text-caption grey--text">Código</div>
              <div class="font-weight-bold">{{ detailsBono.codigo || "N/A" }}</div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-caption grey--text">Nombre</div>
              <div class="font-weight-bold">{{ detailsBono.nombre || "N/A" }}</div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-caption grey--text">Concepto</div>
              <div class="font-weight-medium">{{ detailsBono.nombreConceptoBono || ("Concepto " + (detailsBono.idConceptoBono || "N/A")) }}</div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-caption grey--text">Monto</div>
              <div class="font-weight-medium">{{ fmtCurrency(detailsBono.total) }}</div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-caption grey--text">Periodicidad</div>
              <div class="font-weight-medium">{{ fmtPeriodicidad(detailsBono.periodicidadTipo, detailsBono.periodicidadConfig) }}</div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-caption grey--text">Tipo de cálculo</div>
              <div class="font-weight-medium">{{ fmtTipoCalculo(detailsBono.tipoCalculo) }}</div>
            </v-col>
            <v-col cols="12">
              <div class="text-caption grey--text">Personas asignadas</div>
              <div class="font-weight-medium">
                {{ (detailsBono.idsPersonas && detailsBono.idsPersonas.length) ? detailsBono.idsPersonas.length + " persona(s)" : "Sin asignaciones" }}
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text color="primary" @click="detailsDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <DialogBono v-model="dialogBono" :bono="bonoEdit" @reload="initialize()" />
    <DialogPersonaBonos v-model="dialogPersonaBonos" :persona="personaSeleccionada" @reload="initialize()"/>
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="6000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios';
import { mapState } from 'vuex';
import DialogBono from './DialogBono.vue';
import DialogPersonaBonos from './DialogPersonaBonos.vue';

export default {
  components: {
    DialogBono,
    DialogPersonaBonos,
  },
  data() {
    return {
      dialogBono: false,
      bonoEdit: null,

      bonosSearch: "",
      bonoConceptoSearch: [],
      personasAAsignar: null,
      personasConBonos: [],

      selectedBonoId: 1,

      assignDialog: false,
      detailsDialog: false,
      deleteDialog: false,

      assignSearch: "",
      peopleSearch: null,
      bonoSeleccionado: {},

      detailsBono: null,

      bonos: [],
      personas: [],
      areas: [],
      conceptosExtra: [
        {
          idConceptoBono: 1,
          icon: "mdi-engine-off",
          color: "teal",
          detalles: "Premia continuidad operativa reduciendo detenciones de máquina."
        },
        {
          idConceptoBono: 2,
          icon: "mdi-timer-off",
          color: "red",
          detalles: "Limita minutos de inactividad atribuible al operador."
        },
        {
          idConceptoBono: 3,
          icon: "mdi-clock-check-outline",
          color: "green",
          detalles: "Cuenta días de llegada a tiempo según tolerancia."
        },
        {
          idConceptoBono: 4,
          icon: "mdi-account-check",
          color: "orange",
          detalles: "Evalúa presencia en periodo y faltas permitidas."
        },
        {
          idConceptoBono: 5,
          icon: "mdi-speedometer",
          color: "blue",
          detalles: "Meta de desempeño vs esperado."
        },
        {
          idConceptoBono: 6,
          icon: "mdi-clipboard-check-outline",
          color: "teal",
          detalles: "Puntaje de uso correcto del sistema."
        },
        {
          idConceptoBono: 7,
          icon: "mdi-file-check-outline",
          color: "deep-orange",
          detalles: "Premia no editar paros/producción (o con máximo permitido)."
        }
      ],

      dialogPersonaBonos: false,
      personaSeleccionada: null,

      asignacionHeaders: [
        { text: "Nombre", value: "nombre" },
        { text: "Paterno", value: "paterno" },
        { text: "Materno", value: "materno" },
        { text: "Turno", value: "turno" },
        { text: "Habilidades", value: "habilidades" },
        { text: "Áreas", value: "areas" },
        { text: "Puesto", value: "puesto" },
        { text: "Requisitos", value: "requisitos", sortable: false }
      ],
      bonosHeaders: [
        { text: "Bono", value: "bono", sortable: false },
        { text: "Periodicidad", value: "periodicidad", align: "center", sortable: false },
        { text: "Tipo Cálculo", value: "tipoCalculo", align: "center", sortable: false },
        { text: "Personas Asignadas", value: "personas", align: "center", sortable: false },
        // { text: "Desde", value: "fechaCreacion", align: "center", sortable: false, },
        { text: "Monto", value: "total", align: "center", sortable: false, },
        { text: "Conceptos", value: "conceptos", align: "center", sortable: false, },
        { text: "Acciones", value: "acciones", align: "center", width: 160, sortable: false }
      ],

      loadingBonos: false,
      loadingPersonas: false,
      loadingArea: false,
      loadingConceptoBonos: false,
      loadingEliminarBono: false,
      loadingAsignarBono: false,
      loadingPersonasBonos: false,
      loadingExcel: false,
      personasBonosError: "",
      snackbar: {
        show: false,
        text: "",
        color: "error",
      },
    };
  },
  computed: {
    ...mapState(["token"]),
    bonoSelectItems() {
      return this.bonos.map(function (b) {
        return { value: b.id, text: b.nombre, sub: b.subtitulo, icon: (b.conceptos && b.conceptos[0]) ? b.conceptos[0].icon : "mdi-tag", color: (b.conceptos && b.conceptos[0]) ? b.conceptos[0].color : "grey" };
      });
    },

    selectedBono() {
      for (var i = 0; i < this.bonos.length; i++) {
        if (this.bonos[i].id === this.selectedBonoId) return this.bonos[i];
      }
      return null;
    },

    filteredBonos() {
      var s = (this.bonosSearch || "").toLowerCase().trim();
      var g = Array.isArray(this.bonoConceptoSearch) ? this.bonoConceptoSearch : [];

      return this.bonos.filter(function (b) {
        var byText =
          !s ||
          (b.nombre || "").toLowerCase().includes(s) ||
          (b.subtitulo || "").toLowerCase().includes(s);

        var byGlobal =
          !g.length || g.map(Number).includes(Number(b.idConceptoBono));

        var byStatus = true;

        return byText && byGlobal && byStatus;
      }, this);
    },

    bonosTableItems() {
      var items = this.filteredBonos.slice();

      var idx = -1;
      for (var i = 0; i < items.length; i++) {
        if (items[i].id === this.selectedBonoId) {
          idx = i;
          break;
        }
      }

      if (idx > 0) {
        var picked = items.splice(idx, 1)[0];
        items.unshift(picked);
      }

      // 1) Preferir catálogo ya mergeado (API + extras) => this.conceptosBonos
      var catById = {};
      var cat = Array.isArray(this.conceptosBonos) ? this.conceptosBonos : [];
      for (var j = 0; j < cat.length; j++) {
        catById[cat[j].idConceptoBono] = cat[j];
      }

      // 2) Fallback si aún no cargó conceptosBonos: usar conceptosExtra
      var extraById = {};
      var extra = Array.isArray(this.conceptosExtra) ? this.conceptosExtra : [];
      for (var k = 0; k < extra.length; k++) {
        extraById[extra[k].idConceptoBono] = extra[k];
      }

      return items.map(function (b) {
        var copy = Object.assign({}, b);
        copy.bono = b.nombre;

        var bc = Array.isArray(copy.bonoConceptos) ? copy.bonoConceptos : [];

        // Enriquecer bonoConceptos para el slot (icon/color/nombre)
        copy.bonoConceptos = bc.map(function (c) {
          var id = c.idConceptoBono;

          var catItem = catById[id];
          var exItem = extraById[id];

          var icon = (catItem && catItem.icon) || (exItem && exItem.icon) || "mdi-tag";
          var color = (catItem && catItem.color) || (exItem && exItem.color) || "grey";

          // Nombre para tooltip: preferir detalles; si no, descripción del concepto
          var nombre =
            (catItem && (catItem.detalles || catItem.NombreUI)) ||
            (exItem && exItem.detalles) ||
            c.descripcion ||
            "";

          return Object.assign({}, c, {
            icon: icon,
            color: color,
            nombre: nombre
          });
        });

        return copy;
      });
    },
    conceptosExtraById() {
      return (this.conceptosExtra || []).reduce((acc, c) => {
        acc[Number(c.idConceptoBono)] = c
        return acc
      }, {})
    },

    filteredPeople() {
      var s = (this.peopleSearch || "").toLowerCase().trim();
      var g = null;

      // return this.personas.filter(function (p) {
      return this.personasConBonos.filter(function (p) {
        var nombre = String(p.nombre || "").toLowerCase();
        var paterno = String(p.paterno || "").toLowerCase();
        var materno = String(p.materno || "").toLowerCase();
        var area = String(p.area || "").toLowerCase();
        var base =
          !s ||
          nombre.includes(s) ||
          paterno.includes(s) ||
          materno.includes(s);

        var global =
          !g ||
          nombre.includes(g) ||
          area.includes(g);

        return base && global;
      });
    },

    peopleListItems() {
      return (this.filteredPeople || []).map(p => ({
        ...p,
        montoPagado: Number(p.montoPagado || 0),
        montoPendiente: Number(p.montoPendiente || 0),
        noAplica: Number(p.noAplica || 0),
      }))
    },


    // Summary
    totalAPagar() {
      return this.bonos.reduce(function (acc, b) {
        return acc + (b.activo ? b.montoTotal : 0);
      }, 0);
    },
    bonosActivosCount() {
      return this.bonos.filter(function (b) { return b.activo; }).length;
    },
    personasConBonoCount() {
      return this.personasConBonos.length;
    },
    bonoMasCaroMonto() {
      var act = this.bonos;
      if (!act.length) return 0;
      return Math.max.apply(null, act.map(function (b) { return b.total; }));
    },
    bonoMasBaratoMonto() {
      var act = this.bonos;
      if (!act.length) return 0;
      return Math.min.apply(null, act.map(function (b) { return b.total; }));
    },
    areasById() {
      return this.areas.reduce((acc, a) => {
        acc[a.idArea] = a.nombre
        return acc
      }, {})
    },
    personasConRequisitos() {
      return (this.personas || []).map(p => {
        const res = this.validarRequisitosPersona(p, this.bonoSeleccionado)
        return { ...p, cumple: res.cumple, razonesNoCumple: res.razones }
      })
    },
  },
  methods: {
    initialize() {
      this.getConceptosBonos();
      this.getBonos();
      this.getBonosPersonas();
      this.getPersonas();
      this.getAreas();
      // this.getExcel();
    },
    getBonos() {
      this.bonos = [];
      this.loadingBonos = true;
      axios
        .get("Bonos/Listar")
        .then(response => {
          this.bonos = response.data
        })
        .catch(error => { console.log(error.response) })
        .finally(() => {
          this.loadingBonos = false;
        })
    },
    getPersonas() {
      this.loadingPersonas = true;
      axios
        .get("/Personas")
        .then(response => {
          this.personas = response.data
        })
        .catch(error => { console.log(error.response) })
        .finally(() => {
          this.loadingPersonas = false;
        })
    },
    getBonosPersonas() {
      this.personasConBonos = [];
      this.loadingPersonasBonos = true;
      this.personasBonosError = "";
      axios
        .get("/Bonos/ListarPersonasConBonos")
        .then(response => {
          this.personasConBonos = Array.isArray(response.data) ? response.data : []
        })
        .catch(error => {
          console.log(error.response)
          const data = error && error.response ? error.response.data : null;
          const trackId = data && data.trackId ? String(data.trackId) : "";
          const msg = data && data.msg ? String(data.msg) : "No fue posible cargar personas con bonos.";
          this.personasBonosError = trackId ? (msg + " TrackId: " + trackId) : msg;
          this.showSnackbar(this.personasBonosError, "error");
        })
        .finally(() => {
          this.loadingPersonasBonos = false;
        })
    },
    getAreas() {
      this.loadingArea = true;
      axios
        .get("Areas")
        .then(response => {
          this.areas = response.data
        })
        .catch(error => {
          console.log(error)
        })
        .finally(() => {
          this.loadingArea = false;
        })
    },
    getConceptosBonos() {
      this.loadingConceptoBonos = true;

      axios
        .get("/Bonos/ListarConceptoBonos")
        .then(response => {
          this.conceptosBonos = response.data;
          // var lista = response.data || [];

          // // Mapa: idConceptoBono -> extra
          // var extraById = {};
          // for (var i = 0; i < this.conceptosExtra.length; i++) {
          //   var ex = this.conceptosExtra[i];
          //   extraById[ex.idConceptoBono] = ex;
          // }

          // // Renombres UI por idConceptoBono
          // var uiNameById = {
          //   5: "Eficiencia",
          //   6: "Evaluación de uso del sistema"
          // };

          // // Merge respetando el orden del API
          // this.conceptosBonos = lista.map(function (c) {
          //   var id = c.idConceptoBono;
          //   var ex = extraById[id];

          //   return Object.assign({}, c, {
          //     // Para mostrar en UI (no rompe tu backend)
          //     NombreUI: uiNameById[id] ? uiNameById[id] : c.Descripcion,

          //     icon: ex ? ex.icon : "mdi-tag",
          //     color: ex ? ex.color : "grey",
          //     detalles: ex ? ex.detalles : ""
          //   });
          // });
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          this.loadingConceptoBonos = false;
        });
    },
    async getExcel() {
      this.loadingExcel = true
      await this.$nextTick() // para que pinte el loading antes de iniciar

      try {
        const url = "/Bonos/XLSReportePersonasConBonos"

        const res = await axios.get(url, { responseType: "blob" })

        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        })

        const link = document.createElement("a")
        const href = URL.createObjectURL(blob)

        link.href = href
        link.download = "Bonos.xlsx"
        document.body.appendChild(link)
        link.click()
        link.remove()

        URL.revokeObjectURL(href)
      } catch (e) {
        console.log(e)
      } finally {
        this.loadingExcel = false
      }
    },
    deleterBono() {
      this.loadingEliminarBono = true;
      axios
        .put(`Bonos/Desactivar/${this.detailsBono.idBono}`)
        .then(() => {
          this.initialize();
          this.cerrarEliminarBono();
        })
        .catch(error => console.log(error))
        .finally(() => this.loadingEliminarBono = false)
    },
    asignarBono() {
      const ids = (this.personasAAsignar || []).map(x => x.idPersona);
      this.loadingAsignarBono = true;
      axios
        .post(`Bonos/AsignarBonoPersonas/${this.bonoSeleccionado.idBono}`, ids)
        .then(() => {
          this.initialize();
          this.cerrarAsignacionBono();
        })
        .catch(error => console.log(error))
        .finally(() => this.loadingAsignarBono = false)
    },

    abrirCrear() { this.bonoEdit = null; this.dialogBono = true; },
    editarBono(item) {
      this.bonoEdit = null;
      this.bonoEdit = item;
      this.dialogBono = true;
    },
    eliminarBono(item) {
      this.detailsBono = item;
      this.dialogEliminarBono = true;
    },
    cerrarEliminarBono() {
      this.dialogEliminarBono = false
      this.detailsBono = null
    },

    fmtCurrency(value) {
      var n = Number(value || 0);
      return n.toLocaleString("es-MX", { style: "currency", currency: "MXN" });
    },
    fmtTipoCalculo(tipoCalculo) {
      var t = String(tipoCalculo || "").trim().toUpperCase();
      if (!t) return "ESTANDAR";
      return t.replace(/_/g, " ");
    },
    fmtPeriodicidad(periodicidadTipo, periodicidadConfig) {
      var tipo = String(periodicidadTipo || "").trim().toUpperCase();
      var cfg = periodicidadConfig;
      if (typeof cfg === "string") {
        try { cfg = JSON.parse(cfg); } catch (e) { cfg = {}; }
      }
      if (!cfg || typeof cfg !== "object") cfg = {};

      if (tipo === "DIARIO") return "Diario";
      if (tipo === "SEMANAL") {
        var dias = Array.isArray(cfg.diasSemana) ? cfg.diasSemana : [];
        return dias.length ? ("Semanal (" + dias.join(",") + ")") : "Semanal";
      }
      if (tipo === "QUINCENAL") {
        var quincenas = Array.isArray(cfg.quincenas) ? cfg.quincenas : [];
        return quincenas.length ? ("Quincenal (" + quincenas.join(",") + ")") : "Quincenal";
      }
      if (tipo === "MENSUAL") {
        var dia = Number(cfg.diaMes);
        if (Number.isFinite(dia)) return dia === 0 ? "Mensual (último día)" : ("Mensual (día " + dia + ")");
        return "Mensual";
      }
      return "Mensual";
    },

    abrirAsignacionBono(item) {
      this.bonoSeleccionado = JSON.parse(JSON.stringify({}));

      if (item) {
        this.bonoSeleccionado = item;

        const set = new Set((item.idsPersonas || []).map(Number));
        this.personasAAsignar = (this.personasConRequisitos || []).filter(p =>
          set.has(Number(p.idPersona))
        );
      }

      this.assignDialog = true;
    },
    cerrarAsignacionBono() {
      this.bonoSeleccionado = JSON.parse(JSON.stringify({}));
      this.assignDialog = false;
    },


    openDetails(bono) {
      this.detailsBono = bono;
      this.detailsDialog = true;
    },

    _toNumArray(arr, key) {
      if (!Array.isArray(arr)) return []
      return arr
        .map(x => (key && x && typeof x === "object" ? x[key] : x))
        .map(x => Number(x))
        .filter(n => Number.isFinite(n))
    },

    _intersect(a, b) {
      if (!a.length || !b.length) return false
      const setB = new Set(b)
      return a.some(x => setB.has(x))
    },

    validarRequisitosPersona(persona, bono) {
      const razones = []
      if (!bono || !persona) return { cumple: true, razones }

      // (Opcional pero recomendado) persona activa
      if (persona.condicion === false || persona.estado === false) {
        razones.push("La persona está inactiva.")
        return { cumple: false, razones }
      }

      // Requeridos del bono (si vienen vacíos -> no se comparan)
      const reqPuestos = this._toNumArray(bono.idsPuesto)
      const reqTurnos = this._toNumArray(bono.idsTurno)
      const reqAreas = this._toNumArray(bono.idsArea)
      const reqMaquinas = this._toNumArray(bono.idsMaquina)
      const reqHabilidades = this._toNumArray(bono.idsHabilidad)

      // Datos de la persona (normalizados)
      const personaPuesto = Number(persona.idPuesto)
      const personaTurno = Number(persona.idTurno)

      // persona.areas puede venir como [1,2] o [{idArea:1}]
      const personaAreas = this._toNumArray(persona.areas, "idArea")

      // persona.maquinas puede venir null, [1,2] o [{idMaquina:1}]
      const personaMaquinas = this._toNumArray(persona.maquinas, "idMaquina")

      // persona.habilidades puede venir [] o [{idHabilidad:1}] o [1,2]
      const personaHabilidades = this._toNumArray(persona.habilidades, "idHabilidad")

      // Puesto: persona debe estar en alguno de los puestos seleccionados
      if (reqPuestos.length && !reqPuestos.includes(personaPuesto)) {
        razones.push(`Puesto no coincide (persona: ${persona.puesto || personaPuesto}).`)
      }

      // Turno: persona debe estar en alguno de los turnos seleccionados
      if (reqTurnos.length && !reqTurnos.includes(personaTurno)) {
        razones.push(`Turno no coincide (persona: ${persona.turno || personaTurno}).`)
      }

      // Área: persona debe pertenecer a alguna de las áreas seleccionadas
      if (reqAreas.length && !this._intersect(reqAreas, personaAreas)) {
        razones.push("Área no coincide.")
      }

      // Máquina: persona debe tener al menos 1 máquina de las seleccionadas
      if (reqMaquinas.length && !this._intersect(reqMaquinas, personaMaquinas)) {
        razones.push("Máquina no coincide.")
      }

      // Habilidades: si el bono tiene habilidades seleccionadas, la persona debe tener TODAS
      if (reqHabilidades.length) {
        const setHab = new Set(personaHabilidades)
        const faltantes = reqHabilidades.filter(id => !setHab.has(id))
        if (faltantes.length) {
          razones.push(`Faltan habilidades: ${faltantes.join(", ")}.`)
        }
      }

      return { cumple: razones.length === 0, razones }
    },
    getConceptBase(id) {
      return this.conceptosExtraById ? this.conceptosExtraById[Number(id)] : null
    },
    parseJsonObjectSafe(raw) {
      if (!raw) return null
      if (typeof raw === "object") return raw
      if (typeof raw !== "string") return null
      try {
        return JSON.parse(raw)
      } catch (e) {
        return null
      }
    },
    getBonoKpiConfig(bono) {
      if (!bono || typeof bono !== "object") return null
      const candidates = [
        bono.kpiConfig,
        bono.configuracionKpi,
        bono.configKpi,
        bono.kpiRulesJson
      ]
      for (let i = 0; i < candidates.length; i++) {
        const parsed = this.parseJsonObjectSafe(candidates[i])
        if (parsed && typeof parsed === "object") return parsed
      }
      return null
    },
    isKpiBono(bono) {
      if (!bono || typeof bono !== "object") return false
      if (bono.usarKpiBuilder === true) return true
      const cfg = this.getBonoKpiConfig(bono)
      if (!cfg) return false
      const rules = Array.isArray(cfg.rules) ? cfg.rules : []
      const kpis = Array.isArray(cfg.kpis) ? cfg.kpis : []
      return rules.length > 0 || kpis.length > 0 || String(cfg.tipo || "").toUpperCase() === "KPI_OPERATIVO"
    },
    getBonoConceptColor(bono, variant) {
      if (this.isKpiBono(bono)) return "indigo " + variant
      return this.getConceptColor(bono && bono.idConceptoBono, variant)
    },
    getBonoConceptIcon(bono) {
      if (this.isKpiBono(bono)) return "mdi-chart-line"
      return this.getConceptIcon(bono && bono.idConceptoBono)
    },
    getBonoConceptTitle(bono) {
      if (this.isKpiBono(bono)) return "Bono KPI Operativo"
      return (bono && bono.nombreConceptoBono) || ("Concepto " + (bono && bono.idConceptoBono))
    },
    getBonoConceptDetalles(bono) {
      if (!this.isKpiBono(bono)) return this.getConceptDetalles(bono && bono.idConceptoBono)
      const cfg = this.getBonoKpiConfig(bono) || {}
      const fromKpis = Array.isArray(cfg.kpis) ? cfg.kpis : []
      const fromRules = Array.isArray(cfg.rules) ? cfg.rules.map(r => String(r.kpi || "").toUpperCase()).filter(Boolean) : []
      const all = Array.from(new Set([].concat(fromKpis, fromRules)))
      return all.length ? ("Indicadores: " + all.join(", ")) : "Indicadores KPI configurados"
    },
    getBonoKpiMetaLine(bono) {
      const cfg = this.getBonoKpiConfig(bono) || {}
      const dias = Number(cfg.periodoDias || 0)
      if (Number.isFinite(dias) && dias > 0) {
        return "Periodo evaluado: " + dias + " dia(s)"
      }
      return "Periodo evaluado: por periodicidad del bono"
    },
    openPerson(p) {
      this.personaSeleccionada = p   // p debe traer "bonos" con "calendario"
      this.dialogPersonaBonos = true
    },

    getConceptColor(id, variant) {
      const c = this.getConceptBase(id)
      const base = (c && c.color) ? c.color : "grey"
      return base + " " + variant
    },
    getConceptIcon(id) {
      const c = this.getConceptBase(id)
      return (c && c.icon) ? c.icon : "mdi-help-circle-outline"
    },
    getConceptDetalles(id) {
      const c = this.getConceptBase(id)
      return (c && c.detalles) ? c.detalles : ""
    },
    showSnackbar(text, color) {
      this.snackbar.text = text || "";
      this.snackbar.color = color || "error";
      this.snackbar.show = true;
    },
  },
  mounted() {
    this.initialize();
  }
};
</script>

<style scoped>
.bonos-root {
  background: #f5f6fb;
  min-height: 100vh;
  padding-bottom: 24px;
}

.bonos-search {
  max-width: 520px;
}

.summary-card,
.bonos-card {
  border-radius: 14px;
}

.w-100 {
  width: 100%;
}

/* Evita usar "gap" por compatibilidad si tu build trae problemas */
.tools-row>* {
  margin-left: 12px;
}

.tools-row>*:first-child {
  margin-left: 0;
}

.bono-select {
  min-width: 260px;
}

.filters-row>* {
  margin-right: 14px;
}

.filters-row>*:last-child {
  margin-right: 0;
}

.filter-input {
  min-width: 260px;
  max-width: 320px;
}

.filter-select {
  min-width: 260px;
  max-width: 320px;
}

.filters-row {
  flex-wrap: wrap;
  align-items: center;
}

.filters-row .filter-input,
.filters-row .filter-select {
  margin-bottom: 12px;
}

@media (max-width: 960px) {
  .filter-input,
  .filter-select {
    min-width: 100%;
    max-width: 100%;
  }
}

.assign-row>* {
  margin-right: 12px;
}

.assign-row>*:last-child {
  margin-right: 0;
}

.assign-search {
  max-width: 280px;
}
</style>
